import type { BookingEntry } from '@nono/contract';
import { Badge } from '@/components/ui/badge';
import { formatBookedAt, formatMetric } from '@/lib/format';
import { useViewMessages } from '@/lib/i18n';

/*
 * The one way a list of named bookings is rendered (Overview KPI card, per-day
 * ad popovers, the Ads-page expansion). Each row is a 3-zone grid that never
 * collapses: fixed time column · flexible identity column (min-w-0, truncates)
 * · right-aligned chip stack. Long lists cut at `maxRows` with a "+N more"
 * line; scrolling is the host card's job, not this list's.
 */

const MESSAGES = {
  nb: {
    empty: 'Ingen bookinger i perioden.',
    more: (n: string) => `+${n} til`,
    memberSale: (memberId: number, saleId: number) => `medlem #${memberId} · salg #${saleId}`,
    saleOnly: (saleId: number) => `salg #${saleId}`,
    internal: 'intern',
    sessionProven: 'Bekreftet i økt (booking-beacon)',
    heuristicMethod: 'Heuristisk kobling — siste uparede annonseklikk i vinduet',
  },
  en: {
    empty: 'No bookings in the period.',
    more: (n: string) => `+${n} more`,
    memberSale: (memberId: number, saleId: number) => `member #${memberId} · sale #${saleId}`,
    saleOnly: (saleId: number) => `sale #${saleId}`,
    internal: 'internal',
    sessionProven: 'Confirmed in session (booking beacon)',
    heuristicMethod: 'Heuristic pairing — most recent unclaimed ad click in the window',
  },
};

type Msgs = (typeof MESSAGES)['nb'];

const AD_LABEL_MAX = 30;

export function BookingList({
  bookings,
  maxRows = 14,
  emptyText,
}: {
  bookings: BookingEntry[];
  maxRows?: number;
  /** Overrides the built-in empty-state copy (e.g. "no named match" wording). */
  emptyText?: string;
}) {
  const m = useViewMessages(MESSAGES);

  if (bookings.length === 0) {
    return <p className="text-label text-fg-muted">{emptyText ?? m.empty}</p>;
  }

  const visible = bookings.slice(0, maxRows);
  const overflow = bookings.length - visible.length;

  return (
    <>
      <ul className="divide-y divide-border/40">
        {visible.map((b) => (
          <BookingRow key={`${b.saleId}-${b.bookedAt}-${b.name}`} b={b} m={m} />
        ))}
      </ul>
      {overflow > 0 ? (
        <p className="mt-1.5 text-micro text-fg-subtle">{m.more(formatMetric(overflow, 'int'))}</p>
      ) : null}
    </>
  );
}

function BookingRow({ b, m }: { b: BookingEntry; m: Msgs }) {
  // formatBookedAt → 'dd.MM HH:mm'; stack date over clock so neither token can
  // ever wrap mid-value. (Unparseable timestamps render as-is on one line.)
  const ts = formatBookedAt(b.bookedAt);
  const sp = ts.lastIndexOf(' ');
  const datePart = sp > 0 ? ts.slice(0, sp) : ts;
  const clockPart = sp > 0 ? ts.slice(sp + 1) : null;
  return (
    <li className="grid grid-cols-[3.5rem_minmax(0,1fr)_auto] items-start gap-x-3 py-2 sm:grid-cols-[4.5rem_minmax(0,1fr)_auto]">
      <span className="flex flex-col leading-tight">
        <span className="text-label tabular text-fg-subtle">{datePart}</span>
        {clockPart ? <span className="text-micro tabular text-fg-subtle">{clockPart}</span> : null}
      </span>
      <span className="min-w-0">
        <span className="block truncate text-label font-semibold text-fg">{b.name}</span>
        <span className="block truncate text-micro text-fg-subtle">
          {b.memberId !== null ? m.memberSale(b.memberId, b.saleId) : m.saleOnly(b.saleId)}
        </span>
      </span>
      {b.ad || b.internal ? (
        <span className="flex max-w-[40%] shrink-0 flex-col items-end gap-1">
          {b.ad ? <AdChip ad={b.ad} m={m} /> : null}
          {b.internal ? <Badge tone="warn">{m.internal}</Badge> : null}
        </span>
      ) : null}
    </li>
  );
}

/** Compact ad identity chip — ad name (or id); full campaign + pairing-method
 * explanation in the title. A check mark marks session-proven pairings
 * (lp_booked beacon); heuristic ones stay plain. */
function AdChip({ ad, m }: { ad: NonNullable<BookingEntry['ad']>; m: Msgs }) {
  const raw = ad.adName ?? ad.adId ?? '';
  if (!raw) return null;
  const label = raw.length > AD_LABEL_MAX ? `${raw.slice(0, AD_LABEL_MAX - 1)}…` : raw;
  const method =
    ad.method === 'session'
      ? m.sessionProven
      : ad.method === 'heuristic'
        ? m.heuristicMethod
        : null;
  const title = [ad.campaign, method].filter(Boolean).join(' · ');
  return (
    <Badge tone="booking" title={title || undefined}>
      {label}
      {ad.method === 'session' ? <span aria-hidden> ✓</span> : null}
    </Badge>
  );
}
